/**
 * A QR code encoder, written here rather than installed.
 *
 * Why: an invite code is read aloud or typed today, which on a phone is the
 * clumsiest part of joining a session. A QR code turns that into pointing one
 * phone at another. The libraries that do this are fine, but this is a
 * self-contained piece of arithmetic with a fixed specification and no
 * dependencies of its own, and the app deliberately carries none beyond
 * React — see the note on dependencies in the README.
 *
 * Scope, deliberately narrow: byte mode, error correction level M, versions 1
 * to 10. That covers a URL of up to 213 characters, which an invite link is
 * comfortably under. Everything outside that scope throws rather than
 * quietly producing something a scanner cannot read.
 *
 * Level M rather than L: the code is meant to be scanned off a phone screen
 * held up across a table, at an angle, possibly with a fingerprint on it.
 * Fifteen percent recoverable beats seven for eleven percent more modules.
 */
/** Error correction level M: recovers about 15% of a damaged code. */
const EC_LEVEL_BITS = 0b00;
/** Byte mode: four bits, then the length, then the bytes themselves. */
const MODE_BYTE = 0b0100;
export const QR_MIN_VERSION = 1;
export const QR_MAX_VERSION = 10;
const BLOCK_LAYOUTS = {
    1: {
        ecCodewordsPerBlock: 10,
        group1Blocks: 1,
        group1DataCodewords: 16,
        group2Blocks: 0,
        group2DataCodewords: 0,
    },
    2: {
        ecCodewordsPerBlock: 16,
        group1Blocks: 1,
        group1DataCodewords: 28,
        group2Blocks: 0,
        group2DataCodewords: 0,
    },
    3: {
        ecCodewordsPerBlock: 26,
        group1Blocks: 1,
        group1DataCodewords: 44,
        group2Blocks: 0,
        group2DataCodewords: 0,
    },
    4: {
        ecCodewordsPerBlock: 18,
        group1Blocks: 2,
        group1DataCodewords: 32,
        group2Blocks: 0,
        group2DataCodewords: 0,
    },
    5: {
        ecCodewordsPerBlock: 24,
        group1Blocks: 2,
        group1DataCodewords: 43,
        group2Blocks: 0,
        group2DataCodewords: 0,
    },
    6: {
        ecCodewordsPerBlock: 16,
        group1Blocks: 4,
        group1DataCodewords: 27,
        group2Blocks: 0,
        group2DataCodewords: 0,
    },
    7: {
        ecCodewordsPerBlock: 18,
        group1Blocks: 4,
        group1DataCodewords: 31,
        group2Blocks: 0,
        group2DataCodewords: 0,
    },
    8: {
        ecCodewordsPerBlock: 22,
        group1Blocks: 2,
        group1DataCodewords: 38,
        group2Blocks: 2,
        group2DataCodewords: 39,
    },
    9: {
        ecCodewordsPerBlock: 22,
        group1Blocks: 3,
        group1DataCodewords: 36,
        group2Blocks: 2,
        group2DataCodewords: 37,
    },
    10: {
        ecCodewordsPerBlock: 26,
        group1Blocks: 4,
        group1DataCodewords: 43,
        group2Blocks: 1,
        group2DataCodewords: 44,
    },
};
/** Centre coordinates of the alignment patterns, per version. */
const ALIGNMENT_CENTRES = {
    1: [],
    2: [6, 18],
    3: [6, 22],
    4: [6, 26],
    5: [6, 30],
    6: [6, 34],
    7: [6, 22, 38],
    8: [6, 24, 42],
    9: [6, 26, 46],
    10: [6, 28, 50],
};
export class QrCapacityError extends Error {
    constructor(byteLength) {
        super(`${byteLength} bytes do not fit in a version ${QR_MAX_VERSION} QR code`);
        this.name = 'QrCapacityError';
    }
}
/** The block structure for a version at level M. */
export function blockLayoutFor(version) {
    const layout = BLOCK_LAYOUTS[version];
    if (!layout)
        throw new RangeError(`no level M layout for version ${version}`);
    return layout;
}
export function moduleCount(version) {
    return version * 4 + 17;
}
function dataCodewords(version) {
    const layout = BLOCK_LAYOUTS[version];
    return (layout.group1Blocks * layout.group1DataCodewords +
        layout.group2Blocks * layout.group2DataCodewords);
}
/** Versions 1–9 count the byte-mode length in 8 bits, 10 and up in 16. */
function countBits(version) {
    return version < 10 ? 8 : 16;
}
/** The smallest version the payload fits into, or `null` if none does. */
export function smallestVersionFor(byteLength, minVersion = QR_MIN_VERSION) {
    for (let version = Math.max(minVersion, QR_MIN_VERSION); version <= QR_MAX_VERSION; version++) {
        const available = dataCodewords(version) * 8;
        if (4 + countBits(version) + byteLength * 8 <= available)
            return version;
    }
    return null;
}
/* ------------------------------ GF(256) ---------------------------------- */
/*
 * Reed–Solomon works in the field the QR specification names: byte values as
 * polynomials modulo x^8 + x^4 + x^3 + x^2 + 1 (0x11D), with 2 as a generator.
 * Two lookup tables turn multiplication into addition of exponents.
 */
const EXP = new Uint8Array(512);
const LOG = new Uint8Array(256);
{
    let value = 1;
    for (let i = 0; i < 255; i++) {
        EXP[i] = value;
        LOG[value] = i;
        value <<= 1;
        if (value & 0x100)
            value ^= 0x11d;
    }
    // Doubled, so an exponent sum up to 510 needs no modulo at the call site.
    for (let i = 255; i < 512; i++)
        EXP[i] = EXP[i - 255];
}
function gfMultiply(a, b) {
    if (a === 0 || b === 0)
        return 0;
    return EXP[LOG[a] + LOG[b]];
}
/** The generator polynomial for `degree` error correction codewords. */
export function generatorPolynomial(degree) {
    let poly = [1];
    for (let i = 0; i < degree; i++) {
        // Multiply by (x - α^i), which in this field is (x + α^i).
        const next = new Array(poly.length + 1).fill(0);
        for (let j = 0; j < poly.length; j++) {
            // Highest degree first, so the leading coefficient stays 1 and the
            // synthetic division below can use it as the pivot.
            next[j] = next[j] ^ poly[j];
            next[j + 1] = next[j + 1] ^ gfMultiply(poly[j], EXP[i]);
        }
        poly = next;
    }
    return poly;
}
/** The error correction codewords for one block of data codewords. */
export function errorCorrectionCodewords(data, count) {
    const generator = generatorPolynomial(count);
    const remainder = [...data, ...new Array(count).fill(0)];
    for (let i = 0; i < data.length; i++) {
        const factor = remainder[i];
        if (factor === 0)
            continue;
        for (let j = 0; j < generator.length; j++) {
            remainder[i + j] = remainder[i + j] ^ gfMultiply(generator[j], factor);
        }
    }
    return remainder.slice(data.length);
}
/* --------------------------- bits and codewords -------------------------- */
/** The full codeword stream: mode, length, payload, padding, then EC. */
function buildCodewords(bytes, version) {
    const layout = BLOCK_LAYOUTS[version];
    const capacity = dataCodewords(version);
    const bits = [];
    const pushBits = (value, length) => {
        for (let i = length - 1; i >= 0; i--)
            bits.push((value >> i) & 1);
    };
    pushBits(MODE_BYTE, 4);
    pushBits(bytes.length, countBits(version));
    for (const byte of bytes)
        pushBits(byte, 8);
    // Terminator: up to four zero bits, fewer if the capacity is nearly full.
    const terminator = Math.min(4, capacity * 8 - bits.length);
    pushBits(0, terminator);
    // Then to a byte boundary, then the two alternating pad bytes the
    // specification names.
    while (bits.length % 8 !== 0)
        bits.push(0);
    const data = [];
    for (let i = 0; i < bits.length; i += 8) {
        let byte = 0;
        for (let j = 0; j < 8; j++)
            byte = (byte << 1) | bits[i + j];
        data.push(byte);
    }
    const PAD = [0xec, 0x11];
    for (let i = 0; data.length < capacity; i++)
        data.push(PAD[i % 2]);
    return interleave(data, layout);
}
/**
 * Blocks are interleaved rather than concatenated: a scratch across the code
 * then damages a few codewords of every block instead of destroying one block
 * outright, and each block can recover a few.
 */
function interleave(data, layout) {
    const blocks = [];
    let offset = 0;
    for (let i = 0; i < layout.group1Blocks; i++) {
        blocks.push(data.slice(offset, offset + layout.group1DataCodewords));
        offset += layout.group1DataCodewords;
    }
    for (let i = 0; i < layout.group2Blocks; i++) {
        blocks.push(data.slice(offset, offset + layout.group2DataCodewords));
        offset += layout.group2DataCodewords;
    }
    const ecBlocks = blocks.map((block) => errorCorrectionCodewords(block, layout.ecCodewordsPerBlock));
    const result = [];
    const longest = Math.max(...blocks.map((block) => block.length));
    for (let i = 0; i < longest; i++) {
        for (const block of blocks)
            if (i < block.length)
                result.push(block[i]);
    }
    for (let i = 0; i < layout.ecCodewordsPerBlock; i++) {
        for (const block of ecBlocks)
            result.push(block[i]);
    }
    return result;
}
function emptyGrid(size) {
    return Array.from({ length: size }, () => new Array(size).fill(null));
}
function placeFinder(grid, row, column) {
    for (let r = -1; r <= 7; r++) {
        for (let c = -1; c <= 7; c++) {
            const y = row + r;
            const x = column + c;
            if (y < 0 || y >= grid.length || x < 0 || x >= grid.length)
                continue;
            const onRing = (r >= 0 && r <= 6 && (c === 0 || c === 6)) || (c >= 0 && c <= 6 && (r === 0 || r === 6));
            const inCore = r >= 2 && r <= 4 && c >= 2 && c <= 4;
            grid[y][x] = onRing || inCore;
        }
    }
}
function placeAlignment(grid, version) {
    const centres = ALIGNMENT_CENTRES[version];
    for (const row of centres) {
        for (const column of centres) {
            // The three corners already carry a finder pattern.
            if (grid[row][column] !== null)
                continue;
            for (let r = -2; r <= 2; r++) {
                for (let c = -2; c <= 2; c++) {
                    const ring = Math.max(Math.abs(r), Math.abs(c));
                    grid[row + r][column + c] = ring !== 1;
                }
            }
        }
    }
}
function placeTiming(grid) {
    for (let i = 8; i < grid.length - 8; i++) {
        const dark = i % 2 === 0;
        if (grid[6][i] === null)
            grid[6][i] = dark;
        if (grid[i][6] === null)
            grid[i][6] = dark;
    }
}
/** Format information: the EC level and the mask, BCH-protected. */
export function formatBits(mask) {
    const data = (EC_LEVEL_BITS << 3) | mask;
    let value = data << 10;
    for (let i = 4; i >= 0; i--) {
        if ((value >> (i + 10)) & 1)
            value ^= 0x537 << i;
    }
    // The mask below stops a format string of all zeros, which would look like
    // a blank area to a scanner.
    return ((data << 10) | value) ^ 0x5412;
}
/** Version information, present from version 7 up. */
export function versionBits(version) {
    let value = version << 12;
    for (let i = 5; i >= 0; i--) {
        if ((value >> (i + 12)) & 1)
            value ^= 0x1f25 << i;
    }
    return (version << 12) | value;
}
function reserveFormatAreas(grid) {
    const size = grid.length;
    for (let i = 0; i < 9; i++) {
        if (grid[8][i] === null)
            grid[8][i] = false;
        if (grid[i][8] === null)
            grid[i][8] = false;
    }
    for (let i = 0; i < 8; i++) {
        grid[8][size - 1 - i] = grid[8][size - 1 - i] ?? false;
        grid[size - 1 - i][8] = grid[size - 1 - i][8] ?? false;
    }
    // The one module that is always dark, just above the bottom-left finder.
    grid[size - 8][8] = true;
}
function writeFormat(grid, mask) {
    const size = grid.length;
    const bits = formatBits(mask);
    for (let i = 0; i < 15; i++) {
        const bit = ((bits >> i) & 1) === 1;
        // First copy: down the left of the top-left finder, then right along it.
        if (i < 6)
            grid[i][8] = bit;
        else if (i === 6)
            grid[7][8] = bit;
        else if (i === 7)
            grid[8][8] = bit;
        else if (i === 8)
            grid[8][7] = bit;
        else
            grid[8][14 - i] = bit;
        // Second copy, so a damaged corner does not cost the whole code.
        if (i < 8)
            grid[8][size - 1 - i] = bit;
        else
            grid[size - 15 + i][8] = bit;
    }
}
function writeVersion(grid, version) {
    if (version < 7)
        return;
    const size = grid.length;
    const bits = versionBits(version);
    for (let i = 0; i < 18; i++) {
        const bit = ((bits >> i) & 1) === 1;
        const row = Math.floor(i / 3);
        const column = i % 3;
        grid[row][size - 11 + column] = bit;
        grid[size - 11 + column][row] = bit;
    }
}
/** The eight masks, by their condition on a module's coordinates. */
export function maskAt(mask, row, column) {
    switch (mask) {
        case 0:
            return (row + column) % 2 === 0;
        case 1:
            return row % 2 === 0;
        case 2:
            return column % 3 === 0;
        case 3:
            return (row + column) % 3 === 0;
        case 4:
            return (Math.floor(row / 2) + Math.floor(column / 3)) % 2 === 0;
        case 5:
            return ((row * column) % 2) + ((row * column) % 3) === 0;
        case 6:
            return (((row * column) % 2) + ((row * column) % 3)) % 2 === 0;
        default:
            return (((row + column) % 2) + ((row * column) % 3)) % 2 === 0;
    }
}
/**
 * Walks the data region in the order the specification lays down: upward and
 * downward in two-module columns from the bottom right, skipping the vertical
 * timing pattern. Exported because the tests read the codewords back out the
 * same way, which is the only way to check placement without a scanner.
 */
export function dataModulePositions(size, reserved) {
    const positions = [];
    let upward = true;
    for (let right = size - 1; right > 0; right -= 2) {
        // Column 6 is the vertical timing pattern; the pairs step around it.
        const rightColumn = right <= 6 ? right - 1 : right;
        for (let step = 0; step < size; step++) {
            const row = upward ? size - 1 - step : step;
            for (const column of [rightColumn, rightColumn - 1]) {
                if (reserved[row][column] !== null)
                    continue;
                positions.push([row, column]);
            }
        }
        upward = !upward;
    }
    return positions;
}
function penalty(modules) {
    const size = modules.length;
    let score = 0;
    // Rule 1: runs of five or more of the same colour, in both directions.
    const runScore = (line) => {
        let total = 0;
        let run = 1;
        for (let i = 1; i < line.length; i++) {
            if (line[i] === line[i - 1]) {
                run++;
            }
            else {
                if (run >= 5)
                    total += 3 + (run - 5);
                run = 1;
            }
        }
        if (run >= 5)
            total += 3 + (run - 5);
        return total;
    };
    for (let i = 0; i < size; i++) {
        score += runScore(modules[i]);
        score += runScore(modules.map((row) => row[i]));
    }
    // Rule 2: every 2×2 block of one colour.
    for (let row = 0; row < size - 1; row++) {
        for (let column = 0; column < size - 1; column++) {
            const value = modules[row][column];
            if (modules[row][column + 1] === value &&
                modules[row + 1][column] === value &&
                modules[row + 1][column + 1] === value) {
                score += 3;
            }
        }
    }
    // Rule 3: anything that looks like a finder pattern in the data.
    const FINDER_LIKE = [true, false, true, true, true, false, true, false, false, false, false];
    const REVERSED = [...FINDER_LIKE].reverse();
    const matches = (line, pattern, at) => pattern.every((value, index) => line[at + index] === value);
    for (let i = 0; i < size; i++) {
        const row = modules[i];
        const column = modules.map((line) => line[i]);
        for (let at = 0; at + FINDER_LIKE.length <= size; at++) {
            if (matches(row, FINDER_LIKE, at) || matches(row, REVERSED, at))
                score += 40;
            if (matches(column, FINDER_LIKE, at) || matches(column, REVERSED, at))
                score += 40;
        }
    }
    // Rule 4: how far the balance of dark and light is from even.
    const dark = modules.flat().filter(Boolean).length;
    const percent = (dark * 100) / (size * size);
    score += Math.floor(Math.abs(percent - 50) / 5) * 10;
    return score;
}
/**
 * Encodes `text` as a QR code matrix.
 *
 * Throws `QrCapacityError` when the text is too long for version 10 — the
 * caller's answer to that is to show a link instead of a code, never to
 * render something a scanner would refuse.
 */
/**
 * Everything that is fixed before a single bit of data is placed: the three
 * finders, the alignment patterns, the timing lines, the version block and
 * the reserved format areas. Exported because reading a finished symbol back
 * out — which is how the tests check placement without a scanner — needs to
 * know which modules carry data.
 */
export function functionPatternGrid(version) {
    const size = moduleCount(version);
    const grid = emptyGrid(size);
    placeFinder(grid, 0, 0);
    placeFinder(grid, 0, size - 7);
    placeFinder(grid, size - 7, 0);
    placeAlignment(grid, version);
    placeTiming(grid);
    writeVersion(grid, version);
    reserveFormatAreas(grid);
    return grid;
}
export function encodeQr(text, options = {}) {
    const bytes = new TextEncoder().encode(text);
    const version = smallestVersionFor(bytes.length, options.minVersion);
    if (version === null)
        throw new QrCapacityError(bytes.length);
    const size = moduleCount(version);
    const codewords = buildCodewords(bytes, version);
    // The function patterns first: they are what the walk below steps around.
    const reserved = functionPatternGrid(version);
    const positions = dataModulePositions(size, reserved);
    const withData = reserved.map((row) => [...row]);
    positions.forEach(([row, column], index) => {
        const codeword = codewords[index >> 3];
        // Positions past the last codeword are the remainder bits: light, and
        // masked like any other data module.
        const bit = codeword === undefined ? 0 : (codeword >> (7 - (index & 7))) & 1;
        withData[row][column] = bit === 1;
    });
    let best = null;
    for (let mask = 0; mask < 8; mask++) {
        const candidate = withData.map((row) => [...row]);
        for (const [row, column] of positions) {
            if (maskAt(mask, row, column))
                candidate[row][column] = !candidate[row][column];
        }
        writeFormat(candidate, mask);
        const modules = candidate.map((row) => row.map((value) => value === true));
        const score = penalty(modules);
        if (!best || score < best.score)
            best = { modules, score };
    }
    return { version, size, modules: best.modules };
}
